import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { cloneDeep, isEqual } from "lodash";
import { message } from "antd";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import FormatBoldOutlinedIcon from "@mui/icons-material/FormatBoldOutlined";
import FormatUnderlinedOutlinedIcon from "@mui/icons-material/FormatUnderlinedOutlined";
import ArrowCircleUpOutlinedIcon from "@mui/icons-material/ArrowCircleUpOutlined";
import ArrowCircleDownOutlinedIcon from "@mui/icons-material/ArrowCircleDownOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import * as Icons from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./lender-rebrand.css";

const emptyProfileDesign = {
  profile: {
    profile_photo: "",
    company_name: "",
    additionals: [],
    social: [],
  },
  content: [],
};

const normalizeProfileDesign = (design = emptyProfileDesign) => {
  const profile = design.profile || {};

  return {
    profile: {
      ...emptyProfileDesign.profile,
      ...profile,
      additionals: Array.isArray(profile.additionals) ? profile.additionals : [],
      social: Array.isArray(profile.social) ? profile.social : [],
    },
    content: Array.isArray(design.content) ? design.content : [],
  };
};

const MyProfile = () => {
  const iconLibrary = Icons;
  const iconNames = useMemo(() => Object.keys(iconLibrary), [iconLibrary]);
  const [iconsSearchTerm, setIconSearchTerm] = useState("");
  const [focusElement, setFocusElement] = useState(null);
  const [changed, setChanged] = useState(false);
  const [profileElement, setProfileElement] = useState(emptyProfileDesign);
  const [profileElementb4, setProfileElementb4] = useState(emptyProfileDesign);
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    setChanged(!isEqual(profileElement, profileElementb4));
  }, [profileElement, profileElementb4]);

  useEffect(() => {
    getProfileDesign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        !event.target.closest("[data-profile-editor-trigger='true']")
      ) {
        setFocusElement(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateProfileElement = (updater) => {
    setProfileElement((current) => {
      const next = normalizeProfileDesign(cloneDeep(current));
      updater(next);
      return next;
    });
  };

  const handleFileUpload = async (selectedFile) => {
    if (!selectedFile) return "";

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await fetch(
        "https://www.smart-lend.com/api/file/uploadUniversalFile",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: formData,
        }
      );

      const json = await response.json();
      return json.url;
    } catch (error) {
      console.error(error);
      message.error("Unable to upload file");
      return "";
    }
  };

  const getProfileDesign = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/user/getProfileDesign",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );

      const json = await response.json();
      if (json.design !== null) {
        const parsedDesign = normalizeProfileDesign(JSON.parse(json.design));
        setProfileElement(parsedDesign);
        setProfileElementb4(cloneDeep(parsedDesign));
      } else {
        getUserDetails();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const updateProfileDesign = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/user/updateProfileDesign",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            profile_design: JSON.stringify(profileElement),
          }),
        }
      );

      if (response.status === 200) {
        setProfileElementb4(cloneDeep(profileElement));
        setChanged(false);
        message.success("Profile successfully updated");
      }
    } catch (error) {
      console.error(error);
      message.error("Unable to update profile");
    }
  };

  const getUserDetails = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/profile/getUserDetails",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );

      if (response.status === 200) {
        const json = await response.json();
        updateProfileElement((next) => {
          next.profile.company_name = json.User.principal_name;
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  function generateRandomStringWithDateTime(length = 10) {
    const characters =
      "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let randomString = "";
    for (let i = 0; i < length; i++) {
      randomString += characters[Math.floor(Math.random() * characters.length)];
    }

    const date = new Date();
    const formattedDate = date.toISOString().slice(0, 10).replace(/-/g, "");
    const formattedTime = date.toTimeString().slice(0, 8).replace(/:/g, "");

    return randomString + formattedDate + formattedTime;
  }

  const profile = profileElement.profile || emptyProfileDesign.profile;
  const content = profileElement.content || [];
  const displayName = profile.company_name || "Sample 1";
  const initials = getInitials(displayName);
  const subDescriptions = profile.additionals || [];
  const visibleSubDescriptions = subDescriptions
    .map((item) => item.text?.trim())
    .filter(Boolean);
  const profileStrength = Math.min(
    100,
    42 +
      (profile.profile_photo ? 16 : 0) +
      Math.min(profile.additionals?.length || 0, 3) * 8 +
      Math.min(profile.social?.length || 0, 4) * 5 +
      Math.min(content.length, 3) * 6
  );

  const addAdditional = () => {
    updateProfileElement((next) => {
      next.profile.additionals.push({
        id: generateRandomStringWithDateTime(),
        text: "",
      });
    });
  };

  const addSocial = () => {
    const id = generateRandomStringWithDateTime();
    updateProfileElement((next) => {
      next.profile.social.push({
        id,
        icon: "faFacebook",
        link: "",
      });
    });
    setIconSearchTerm("");
    setFocusElement(id);
  };

  const addContent = (type) => {
    const base = {
      id: generateRandomStringWithDateTime(),
      type,
      mt: 0,
      mb: 0,
    };

    const itemToPush =
      type === "text"
        ? {
            ...base,
            text: "Type your text here",
            fontWeight: 400,
            underline: false,
          }
        : type === "button"
        ? { ...base, text: "Button text", url: "" }
        : { ...base, url: "" };

    updateProfileElement((next) => {
      next.content.push(itemToPush);
    });
  };

  const moveContent = (fromIndex, direction) => {
    updateProfileElement((next) => {
      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= next.content.length) return;
      const [item] = next.content.splice(fromIndex, 1);
      next.content.splice(toIndex, 0, item);
    });
  };

  const renderSocialIcon = (soci, index, editable = false) => {
    const icon = iconLibrary[soci.icon] || iconLibrary.faFacebook;

    return (
      <div
        key={soci.id || index}
        className="sl-profile-social-item"
        data-profile-editor-trigger={editable ? "true" : undefined}
        onClick={
          editable
            ? () => {
                setIconSearchTerm("");
                setFocusElement(soci.id);
              }
            : undefined
        }
      >
        <FontAwesomeIcon icon={icon} />
        {editable && focusElement === soci.id ? renderSocialPicker(soci, index) : null}
      </div>
    );
  };

  const renderSocialPicker = (soci, index) => {
    const visibleIcons =
      iconsSearchTerm === ""
        ? iconNames.slice(0, 153)
        : iconNames.filter((iconName) =>
            iconName.toLowerCase().includes(iconsSearchTerm.toLowerCase())
          );

    return (
      <div className="sl-profile-popover sl-profile-social-popover" ref={popoverRef}>
        <label className="sl-profile-popover-label">URL Link</label>
        <input
          className="sl-profile-input"
          placeholder="https://www."
          value={soci.link || ""}
          onChange={(e) => {
            updateProfileElement((next) => {
              next.profile.social[index].link = e.target.value;
            });
          }}
        />
        <label className="sl-profile-popover-label">Social Icon</label>
        <input
          className="sl-profile-input"
          placeholder="Search icons"
          value={iconsSearchTerm}
          onChange={(e) => setIconSearchTerm(e.target.value)}
        />
        <div className="sl-profile-icon-grid">
          {visibleIcons.map((iconName) => {
            const Icon = iconLibrary[iconName];
            return (
              <button
                type="button"
                key={iconName}
                className="sl-profile-icon-choice"
                onClick={() => {
                  updateProfileElement((next) => {
                    next.profile.social[index].icon = iconName;
                  });
                }}
              >
                <FontAwesomeIcon icon={Icon} />
              </button>
            );
          })}
        </div>
        <div className="sl-profile-popover-actions">
          <button
            type="button"
            className="sl-profile-btn"
            onClick={() => {
              setFocusElement(null);
              updateProfileElement((next) => {
                next.profile.social.splice(index, 1);
              });
            }}
          >
            Delete
          </button>
          {/* <button
            type="button"
            className="sl-profile-btn sl-profile-btn-primary"
            onClick={() => setFocusElement(null)}
          >
            Save
          </button> */}
        </div>
      </div>
    );
  };

  const renderBlockEditor = (cont, index) => (
    <div className="sl-profile-popover sl-profile-block-popover" ref={popoverRef}>
      <div className="sl-profile-popover-title">
        {cont.type === "button"
          ? "Button Styling"
          : cont.type === "image"
          ? "Image Styling"
          : "Text Styling"}
      </div>
      <div className="sl-profile-popover-grid">
        <div>
          <span>Margin Top ({cont.mt}px)</span>
          <div className="sl-profile-icon-actions">
            <RemoveCircleOutlineOutlinedIcon
              onClick={() => {
                updateProfileElement((next) => {
                  next.content[index].mt -= 2;
                });
              }}
            />
            <AddCircleOutlineOutlinedIcon
              onClick={() => {
                updateProfileElement((next) => {
                  next.content[index].mt += 2;
                });
              }}
            />
          </div>
        </div>
        <div>
          <span>Margin Bottom ({cont.mb}px)</span>
          <div className="sl-profile-icon-actions">
            <RemoveCircleOutlineOutlinedIcon
              onClick={() => {
                updateProfileElement((next) => {
                  next.content[index].mb -= 2;
                });
              }}
            />
            <AddCircleOutlineOutlinedIcon
              onClick={() => {
                updateProfileElement((next) => {
                  next.content[index].mb += 2;
                });
              }}
            />
          </div>
        </div>
      </div>

      <div className="sl-profile-popover-grid">
        {cont.type === "text" ? (
          <div>
            <span>Text Decoration</span>
            <div className="sl-profile-icon-actions">
              <FormatBoldOutlinedIcon
                className={cont.fontWeight === 400 ? "" : "is-active"}
                onClick={() => {
                  updateProfileElement((next) => {
                    next.content[index].fontWeight =
                      next.content[index].fontWeight === 400 ? 600 : 400;
                  });
                }}
              />
              <FormatUnderlinedOutlinedIcon
                className={cont.underline ? "is-active" : ""}
                onClick={() => {
                  updateProfileElement((next) => {
                    next.content[index].underline = !next.content[index].underline;
                  });
                }}
              />
            </div>
          </div>
        ) : null}
        <div>
          <span>Element Position</span>
          <div className="sl-profile-icon-actions">
            <ArrowCircleDownOutlinedIcon onClick={() => moveContent(index, 1)} />
            <ArrowCircleUpOutlinedIcon onClick={() => moveContent(index, -1)} />
          </div>
        </div>
      </div>

      {cont.type === "button" ? (
        <>
          <label className="sl-profile-popover-label">Button Text</label>
          <input
            className="sl-profile-input"
            placeholder="Button text"
            value={cont.text || ""}
            onChange={(e) => {
              updateProfileElement((next) => {
                next.content[index].text = e.target.value;
              });
            }}
          />
          <label className="sl-profile-popover-label">Button URL</label>
          <input
            className="sl-profile-input"
            placeholder="https://www."
            value={cont.url || ""}
            onChange={(e) => {
              updateProfileElement((next) => {
                next.content[index].url = e.target.value;
              });
            }}
          />
        </>
      ) : null}

      {cont.type === "image" ? (
        <>
          <label className="sl-profile-popover-label">Image Source</label>
          <input
            className="sl-profile-input"
            type="file"
            onChange={async (e) => {
              const uploadedUrl = await handleFileUpload(e.target.files[0]);
              if (!uploadedUrl) return;
              updateProfileElement((next) => {
                next.content[index].url = uploadedUrl;
              });
            }}
          />
        </>
      ) : null}

      <div className="sl-profile-popover-actions">
        <button
          type="button"
          className="sl-profile-btn"
          onClick={() => {
            setFocusElement(null);
            updateProfileElement((next) => {
              next.content.splice(index, 1);
            });
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );

  const renderEditableBlock = (cont, index) => {
    const commonStyle = {
      marginTop: `${Number(cont.mt) || 0}px`,
      marginBottom: `${Number(cont.mb) || 0}px`,
    };
    const isEditing = focusElement === cont.id;

    return (
      <div
        key={cont.id || index}
        className={`sl-profile-builder-item ${isEditing ? "is-editing" : ""}`}
        style={{
          "--profile-editor-space":
            isEditing && cont.type === "text"
              ? "220px"
              : isEditing
              ? "300px"
              : "0px",
        }}
      >
        {cont.type === "text" ? (
          <input
            className="sl-profile-content-input"
            data-profile-editor-trigger="true"
            onFocus={() => setFocusElement(cont.id)}
            style={{
              ...commonStyle,
              textDecoration: cont.underline ? "underline" : "none",
              fontWeight: cont.fontWeight === 400 ? 400 : 700,
              fontSize: cont.fontWeight === 400 ? "13px" : "16px",
            }}
            value={cont.text || ""}
            onChange={(e) => {
              updateProfileElement((next) => {
                next.content[index].text = e.target.value;
              });
            }}
          />
        ) : cont.type === "button" ? (
          <button
            type="button"
            className="sl-profile-preview-button"
            data-profile-editor-trigger="true"
            style={commonStyle}
            onClick={() => setFocusElement(cont.id)}
          >
            {cont.text || "Button text"}
          </button>
        ) : (
          <div
            className="sl-profile-image-edit"
            data-profile-editor-trigger="true"
            style={commonStyle}
            onClick={() => setFocusElement(cont.id)}
          >
            {cont.url ? <img src={cont.url} alt="" /> : <span>Image block preview</span>}
          </div>
        )}
        {isEditing ? renderBlockEditor(cont, index) : null}
      </div>
    );
  };

  const renderPreviewBlock = (cont, index) => {
    const commonStyle = {
      marginTop: `${Number(cont.mt) || 0}px`,
      marginBottom: `${Number(cont.mb) || 0}px`,
    };

    if (cont.type === "text") {
      return (
        <p
          key={cont.id || index}
          className="sl-profile-preview-text"
          style={{
            ...commonStyle,
            textDecoration: cont.underline ? "underline" : "none",
            fontWeight: cont.fontWeight === 400 ? 400 : 700,
          }}
        >
          {cont.text}
        </p>
      );
    }

    if (cont.type === "button") {
      return (
        <div key={cont.id || index} style={commonStyle}>
          <button type="button" className="sl-profile-preview-button">
            {cont.text || "Button text"}
          </button>
        </div>
      );
    }

    return (
      <div key={cont.id || index} className="sl-profile-preview-image" style={commonStyle}>
        {cont.url ? <img src={cont.url} alt="" /> : "Image block preview"}
      </div>
    );
  };

  return (
    <div className="sl-profile-page">
      <div className="sl-profile-frame">
        <div className="sl-profile-head">
          <div>
            <h1>My Profile</h1>
            <p>Manage the lender profile borrowers see when comparing offers.</p>
          </div>
          <div className="sl-profile-toolbar">
            {/* <button
              type="button"
              className="sl-profile-btn"
              onClick={() => previewRef.current?.scrollIntoView({ behavior: "smooth" })}
            >
              <VisibilityOutlinedIcon />
              Preview public page
            </button> */}
            <button
              type="button"
              className={`sl-profile-btn sl-profile-btn-primary ${
                changed ? "is-dirty" : ""
              }`}
              onClick={updateProfileDesign}
            >
              Save changes
            </button>
          </div>
        </div>

        <div className="sl-profile-grid">
          <div className="sl-profile-left">
            <article className="sl-profile-card sl-profile-hero">
              <input
                type="file"
                ref={fileInputRef}
                className="sl-profile-file-input"
                onChange={async (e) => {
                  const uploadedUrl = await handleFileUpload(e.target.files[0]);
                  if (!uploadedUrl) return;
                  updateProfileElement((next) => {
                    next.profile.profile_photo = uploadedUrl;
                  });
                }}
              />
              <div className="sl-profile-identity">
                <button
                  type="button"
                  className="sl-profile-avatar"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload logo"
                >
                  {profile.profile_photo ? (
                    <img src={profile.profile_photo} alt="" />
                  ) : (
                    initials
                  )}
                </button>
                <div>
                  <div className="sl-profile-name">{displayName}</div>
                  {visibleSubDescriptions.length > 0 ? (
                    <p>{visibleSubDescriptions.slice(0, 2).join(" · ")}</p>
                  ) : null}
                  <div className="sl-profile-social">
                    {profile.social?.map((soci, index) =>
                      renderSocialIcon(soci, index, true)
                    )}
                    <button
                      type="button"
                      className="sl-profile-social-add"
                      onClick={addSocial}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="sl-profile-score">
                  <span>Profile strength</span>
                  <div
                    className="sl-profile-score-ring"
                    style={{ "--strength": `${profileStrength}%` }}
                  >
                    <b>{profileStrength}%</b>
                  </div>
                </div>
              </div>
            </article>

            <article className="sl-profile-card sl-profile-details-card">
              <div className="sl-profile-section-head">
                <div>
                  <h2>Profile Details</h2>
                  <p>Keep the public description clear, verified, and borrower friendly.</p>
                </div>
              </div>

              <div className="sl-profile-form">
                <label className="sl-profile-field">
                  <span>Display name</span>
                  <input
                    value={displayName}
                    onChange={(e) => {
                      updateProfileElement((next) => {
                        next.profile.company_name = e.target.value;
                      });
                    }}
                  />
                </label>
                {profile.additionals?.map((addi, index) => (
                  <label className="sl-profile-field" key={addi.id || index}>
                    <span>{`Sub description ${index + 1}`}</span>
                    <div className="sl-profile-inline-field">
                      <input
                        value={addi.text || ""}
                        placeholder="Enter sub description"
                        onChange={(e) => {
                          updateProfileElement((next) => {
                            next.profile.additionals[index].text = e.target.value;
                          });
                        }}
                      />
                      <RemoveCircleOutlineOutlinedIcon
                        onClick={() => {
                          updateProfileElement((next) => {
                            next.profile.additionals.splice(index, 1);
                          });
                        }}
                      />
                    </div>
                  </label>
                ))}
              </div>

              <div className="sl-profile-tools">
                <button type="button" className="sl-profile-btn" onClick={addAdditional}>
                  <AddCircleOutlineOutlinedIcon />
                  Add sub-description
                </button>
                <button
                  type="button"
                  className="sl-profile-btn"
                  onClick={() => addContent("text")}
                >
                  Add text block
                </button>
                <button
                  type="button"
                  className="sl-profile-btn"
                  onClick={() => addContent("button")}
                >
                  Add button
                </button>
                <button
                  type="button"
                  className="sl-profile-btn"
                  onClick={() => addContent("image")}
                >
                  Add image
                </button>
                <button
                  type="button"
                  className="sl-profile-btn sl-profile-btn-blue"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadOutlinedIcon />
                  Upload logo
                </button>
              </div>
            </article>

            <section className="sl-profile-block-section">
              <h2>Profile Blocks</h2>
              <div className="sl-profile-builder-blocks">
                {content.length === 0 ? (
                  <div className="sl-profile-builder-empty">
                    Add text, button, or image blocks to shape your public profile.
                  </div>
                ) : (
                  content.map((cont, index) => renderEditableBlock(cont, index))
                )}
              </div>
            </section>
          </div>

          <aside className="sl-profile-preview-shell" ref={previewRef}>
            <h2>Borrower Preview</h2>
            <div className="sl-profile-card sl-profile-preview">
              <div className="sl-profile-preview-cover" />
              <div className="sl-profile-preview-body">
                <div className="sl-profile-preview-avatar">
                  {profile.profile_photo ? (
                    <img src={profile.profile_photo} alt="" />
                  ) : (
                    initials
                  )}
                </div>
                <section className="sl-profile-preview-section">
                  <h2>{displayName}</h2>
                  {visibleSubDescriptions.length > 0 ? (
                    <p>{visibleSubDescriptions.slice(0, 2).join(" · ")}</p>
                  ) : null}
                  <div className="sl-profile-social sl-profile-preview-social">
                    {profile.social?.map((soci, index) =>
                      renderSocialIcon(soci, index, false)
                    )}
                  </div>
                </section>
                <div className="sl-profile-preview-blocks">
                  {content.map((cont, index) => renderPreviewBlock(cont, index))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

const getInitials = (name) => {
  const words = String(name || "Sample 1")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return "S1";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

export default MyProfile;
